import { BrowserRouter as Router } from 'react-router-dom'
import { QueryClient, QueryClientProvider, QueryCache } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import axios from 'axios'
import { AppRoutes } from './AppRoutes'
import { ErrorBoundary, RouteErrorBoundary } from './components/ErrorBoundary'
import { useHealthStore } from './stores/healthStore'
import { useBackendHealthPoller } from './hooks/useBackendHealth'
import { initErrorTracking } from './lib/errorTracking'
import { errorStatusOf } from './lib/errors'
import { SheetStackProvider } from '@/hooks/useSheetStack'
import { ConfirmHost } from '@/components/ui/ConfirmDialog'

// Axios requests feed the network-health store from lib/api.ts. Here we only
// catch non-axios fetches that failed with no response (native TypeError).
// 5xx responses never count as "offline" — they are server faults (UX-016).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // One retry for network blips and 5xx; none for 4xx (a 403 or 404
      // won't change on retry, and the retry delays the error card).
      retry: (failureCount, error) => {
        const status = errorStatusOf(error)
        if (status != null && status >= 400 && status < 500) return false
        return failureCount < 1
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      if (!axios.isAxiosError(error) && error instanceof TypeError) {
        useHealthStore.getState().recordFailure()
      }
    },
  }),
})

// Initialize Sentry once at module load — safe to call with no DSN configured
initErrorTracking()

// Mounts THE network poller. Rendered once near the app root; everything else
// reads the store (useOnline / useBackendHealth) without polling.
function BackendHealthPoller() {
  useBackendHealthPoller()
  return null
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <SheetStackProvider>
          <Router>
            <BackendHealthPoller />
            <RouteErrorBoundary>
              <AppRoutes />
            </RouteErrorBoundary>
            {/* Answers `await confirm({...})` from useConfirm() anywhere. */}
            <ConfirmHost />
          </Router>
        </SheetStackProvider>
      </ErrorBoundary>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  )
}

export default App
